import React, { useMemo } from 'react';
import { useTranslation } from '@lib/useTypedTranslation';
import * as am4charts from '@amcharts/amcharts4/charts';
import { StackedColumnCategoryChart, StackedColumnsCategoryChartProps } from '../../../../components/chart/stackedColumnsCategoryChart';
import { ISmartBatteryAvgDischargeTimeIntervalStats } from '../../../../services/shared/battery/batteryCentricService';
import Card from '@components/card/card';
import '../style.css';
import { createChartTooltipAdapter, getTitle } from './lib';

interface IProps {
  data: ISmartBatteryAvgDischargeTimeIntervalStats,
  isLoading?: boolean
}

interface BatteryAvgDischargeTimeChartData {
  bucket: string,
  count: number
}

const series = [
  {
    visible: true,
    dataKey: 'count' as keyof BatteryAvgDischargeTimeChartData,
    description: 'Batteries Avg Discharge Time Count',
    colour: '#5b9ad2'
  },
];

export function BatteryAverageDischargeTimeChartContainer(props: IProps) {
  const ns = 'batteryEssentialsChart';
  const { t } = useTranslation(ns);

  const chartData: BatteryAvgDischargeTimeChartData[] = useMemo(() => {
    if (!props.data) return [];

    const data = [
      {
        bucket: '0-1',
        count: props.data.hour0To1,
      },
      {
        bucket: '1-2',
        count: props.data.hour1To2,
      },
      {
        bucket: '2-3',
        count: props.data.hour2To3,
      },
      {
        bucket: '3-4',
        count: props.data.hour3To4,
      },
      {
        bucket: '4-5',
        count: props.data.hour4To5,
      },
      {
        bucket: '5-6',
        count: props.data.hour5To6,
      },
      {
        bucket: '6-7',
        count: props.data.hour6To7,
      },
      {
        bucket: '7-8',
        count: props.data.hour7To8,
      },
      {
        bucket: '8-9',
        count: props.data.hour8To9,
      },
      {
        bucket: '9-10',
        count: props.data.hour9To10,
      },
      {
        bucket: '>10',
        count: props.data.hour10AndAbove,
      }
    ];
    return data;
  }, [props.data]);

  const chartProps: StackedColumnsCategoryChartProps<BatteryAvgDischargeTimeChartData> = {
    data: !props.isLoading ? chartData : null,
    series,
    title: getTitle(t, 'BATTERY_AVG_DISCHARGE_TIME_CHART_TITLE'),
    cypressId: 'battery-avg-discharge-time-chart',
    className: 'battery-avg-discharge-time-chart',
    hasLegend: false,
    bottomBorder: false,
    columnWidth: 70,
    yAxisType: 'value',
    useAxisTooltip: true,
    amchartsYAxisNumberFormat: '#.',
    categoryKey: 'bucket',
    drawXAxis: (chart, xAxis) => {
      xAxis.title.text = t('BATTERY_AVG_DISCHARGE_TIME_CHART_LABEL');
      xAxis.renderer.labels.template.fontSize = 12;
      xAxis.renderer.labels.template.horizontalCenter = 'middle';
      xAxis.renderer.labels.template.verticalCenter = 'middle';
      xAxis.renderer.minGridDistance = 20;
      return xAxis;
    },
    drawYAxis: (chart, yAxis) => {
      yAxis.title.text = t('NO_OF_BATTERIES');
      yAxis.title.fontSize = 12;
      yAxis.renderer.labels.template.fontSize = 12;
      yAxis.min = 0;
      yAxis.strictMinMax = false;
      yAxis.renderer.minGridDistance = 30;
      yAxis.maxPrecision = 0;
      return yAxis;
    },
    getAxisTooltipAdapter: () => createChartTooltipAdapter(t)
  };

  return (
    <Card noPadding={false} className="battery-avg-discharge-time-container">
      <StackedColumnCategoryChart<any>
        {...chartProps}
      />
    </Card>
  );
}
